/**
 * Spec 138 — a schedule's OTHER email recipients, client side.
 *
 * The server is authoritative: cb-api's `resolveExtraRecipients` (delivery.js) decides
 * what is stored, and these helpers only MIRROR it so a mistake is caught while the
 * person is still typing rather than as a 400 after they press Create. The messages are
 * the server's own, word for word, so the two can never read differently.
 *
 * No control-character escape appears in this file, by the repo's standing rule (a
 * literal NUL byte was once committed into `reportGridUtils.js`): every control check is
 * a char-code comparison.
 */

/** The cap, after the owner's own address and duplicates are removed (spec 138 §3.2; 25 since spec 201). */
export const MAX_RECIPIENTS = 25

/** The Auth0 ACCESS-token claim cb-api reads for the owner's address (`parseAuth`). */
const EMAIL_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'

/** True if the string carries any C0/C1 control character (cb-api's `hasControlChars`). */
export function hasControlChars(s) {
  const str = String(s ?? '')
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i)
    if (code <= 0x1f || code === 0x7f || (code >= 0x80 && code <= 0x9f)) return true
  }
  return false
}

/** U+0027 — allowed before the `@` only (spec 139 decision 9), as cb-api allows it. */
const APOSTROPHE = "'"

/**
 * One plausible address — cb-api's `isSingleAddress`, mirrored: exactly one `@`, no
 * whitespace, comma, semicolon, angle bracket, double quote or control byte, something on
 * each side, and a dot inside the domain. An apostrophe is allowed before the `@` only
 * (spec 139 decision 9: `o'brien@firm.example` is a real address).
 */
export function isSingleAddress(s) {
  const str = String(s ?? '')
  if (!str || str.length > 254) return false
  if (hasControlChars(str)) return false
  if (/[\s,;<>"\\()[\]]/.test(str)) return false
  const parts = str.split('@')
  if (parts.length !== 2) return false
  const [local, domain] = parts
  if (domain.includes(APOSTROPHE)) return false
  return local.length > 0 && domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.')
}

/** The server's wording for one bad entry (`delivery.js` `resolveExtraRecipients`). */
export function addressError(raw, position) {
  if (hasControlChars(raw)) return `Address ${position} contains characters an email address can't have.`
  const entry = String(raw ?? '').trim()
  const shown = entry.length > 100 ? `${entry.slice(0, 100)}…` : entry
  return `"${shown}" isn't a single email address.`
}

export const TOO_MANY_ERROR = `A schedule can email at most ${MAX_RECIPIENTS} other addresses.`

/** The owner's own address offered as another recipient: their copy already goes there. */
export const OWN_ADDRESS_ERROR = 'That is your own address. You already get your own copy.'

/**
 * Said, visibly, whenever other addresses move a schedule off Link (§3.7: never switched
 * silently). One sentence for both hosts — the create modal and the schedule editor.
 */
export const FORMAT_SWITCH_NOTE = 'Changed to Excel file attached: the other addresses need the report attached, because a link only opens for you.'
export const CSV_FORMAT_SWITCH_NOTE = 'Changed to CSV file attached: the other addresses need the report attached, because a link only opens for you.'

/**
 * Spec 200 — what the first other address switches a Link to, and the sentence that says
 * so: Excel where the report has an Excel file, else its one format (GL Budget is CSV
 * only). Switching a CSV-only report to Excel would ask for a file it cannot attach.
 */
export const recipientsFormatSwitch = (formats = ['xlsx', 'csv']) => (
  formats.includes('xlsx')
    ? { format: 'xlsx', note: FORMAT_SWITCH_NOTE }
    : { format: 'csv', note: CSV_FORMAT_SWITCH_NOTE }
)

/**
 * "1 other address is" / "3 other addresses are" — the editor's sentence for a list that
 * is kept but not emailed while email is off (*review*).
 */
export function keptRecipientsNote(n) {
  return `${n} other address${n === 1 ? ' is' : 'es are'} kept but won’t be emailed while this is off.`
}

const COMMA = 0x2c
const SEMICOLON = 0x3b
const LINE_FEED = 0x0a
const CARRIAGE_RETURN = 0x0d
const SEPARATORS = new Set([COMMA, SEMICOLON, LINE_FEED, CARRIAGE_RETURN])

/** Does pasted text hold more than one address (a comma, semicolon or line break)? */
export function hasSeparator(text) {
  const str = String(text ?? '')
  for (let i = 0; i < str.length; i++) if (SEPARATORS.has(str.charCodeAt(i))) return true
  return false
}

/**
 * A paste of several addresses, split for convenience on commas, semicolons and line
 * breaks (§3.7). Blank pieces are dropped. The SERVER never splits anything — a comma
 * string sent to it is refused — so this is the only place a list becomes entries.
 */
export function splitPasted(text) {
  const str = String(text ?? '')
  const out = []
  let piece = ''
  for (let i = 0; i < str.length; i++) {
    if (SEPARATORS.has(str.charCodeAt(i))) {
      if (piece.trim()) out.push(piece.trim())
      piece = ''
    } else {
      piece += str[i]
    }
  }
  if (piece.trim()) out.push(piece.trim())
  return out
}

/**
 * Add `entries` to `current`, as the server would store them: trimmed, lowercased, the
 * owner's own address left out, duplicates skipped, at most MAX_RECIPIENTS.
 *
 * Returns `{ recipients, error, rejected }`: the new list, the FIRST problem to show
 * (or null), and the entries that were not added, so the input can keep them for the
 * person to fix rather than throwing their typing away.
 */
export function addRecipients(current, entries, ownerAddress) {
  const recipients = [...(current || [])]
  const own = String(ownerAddress ?? '').trim().toLowerCase()
  const rejected = []
  let error = null
  const fail = (raw, message) => { rejected.push(raw); if (!error) error = message }
  const start = (current || []).length
  ;(entries || []).forEach((raw, i) => {
    if (hasControlChars(raw) || !isSingleAddress(String(raw ?? '').trim())) {
      // Numbered by where it would have gone, as the server numbers the list it is sent.
      fail(raw, addressError(raw, start + i + 1))
      return
    }
    const address = String(raw).trim().toLowerCase()
    if (own && address === own) { fail(raw, OWN_ADDRESS_ERROR); return }
    if (recipients.includes(address)) { fail(raw, `${address} is already on the list.`); return }
    if (recipients.length >= MAX_RECIPIENTS) { fail(raw, TOO_MANY_ERROR); return }
    recipients.push(address)
  })
  return { recipients, error, rejected }
}

// ---------------------------------------------------------------------------
// Spec 139 — an owner whose account has no email address can supply one.
//
// Mirrors cb-api's `resolveOwnerAddress` (delivery.js) for an account with NO usable
// email: the server is authoritative and the sentences are its own, word for word.
// ---------------------------------------------------------------------------

/** §2.3's refusal — cb-api `OWNER_ADDRESS_ERRORS.required`, word for word. */
export const OWNER_ADDRESS_REQUIRED = 'Your account has no email address. Enter the address your own copy should go to — Centerbase needs one to send this email.'

/** §2.2 — a control character; the entry is never echoed. cb-api `OWNER_ADDRESS_ERRORS.controlChars`. */
export const OWNER_ADDRESS_CONTROL_CHARS = "Your address contains characters an email address can't have."

/** §2.7's helper text under the "Your email address" input. Never "from you" (spec 137 §2.11). */
export const OWNER_ADDRESS_HELP = 'Your Centerbase sign-in has no email address. Enter the address your own copy should go to — Centerbase also needs it to send to anyone else.'

/**
 * Does this owner need to supply an address? Only when the owner's address is KNOWN to
 * be absent (`ready` with none). `loading` and `unknown` never ask (§2.8): an account
 * whose token could not be read may well have one, and the server would then refuse it.
 */
export function ownerNeedsAddress(owner) {
  return owner?.status === 'ready' && !owner?.address
}

/** What the server will store: trimmed and lowercased. */
export function normalizeOwnerAddress(raw) {
  return String(raw ?? '').trim().toLowerCase()
}

/**
 * The client mirror of `resolveOwnerAddress` for an account with no address: `null` when
 * the value will be accepted, otherwise the server's own sentence. Empty is the §2.3
 * refusal, because the field only exists while email is on.
 */
export function ownerAddressError(raw) {
  const str = String(raw ?? '')
  if (hasControlChars(str)) return OWNER_ADDRESS_CONTROL_CHARS
  const entry = str.trim()
  if (!entry) return OWNER_ADDRESS_REQUIRED
  if (!isSingleAddress(entry.toLowerCase())) {
    const shown = entry.length > 100 ? `${entry.slice(0, 100)}…` : entry
    return `"${shown}" isn't a single email address.`
  }
  return null
}

/**
 * The owner's own address as cb-api will see it, from the decoded ACCESS-token claims —
 * the same claim, in the same order, that `parseAuth` reads (`client.js`). `null` when
 * there is none, or when it is not one usable address (cb-api then sends the owner no
 * copy either: spec 137 deviation 6).
 */
export function ownDeliveryAddress(claims) {
  const raw = String(claims?.[EMAIL_CLAIM] || claims?.email || '').trim()
  return isSingleAddress(raw) ? raw : null
}
